import type { VulpariNode } from "@vulpari/core";

import { formatMonthLabel } from "../lib/format.js";
import { useStore } from "../store.js";
import { MoneyField } from "./MoneyField.js";

/** The primary, override-able figure per node type, and how to label it. */
const OVERRIDE_LABEL: Partial<Record<VulpariNode["type"], string>> = {
  income: "Income",
  bill: "Amount",
  external: "Amount paid",
  card: "Payment",
  pot: "Top-up",
};

function basePrimary(node: VulpariNode): number | null {
  switch (node.type) {
    case "income":
    case "bill":
      return node.meta.amount;
    case "external":
      return node.meta.amount ?? 0;
    case "card":
      return node.meta.payment ?? node.meta.statementBalance ?? 0;
    case "pot":
      return node.meta.topUp ?? 0;
    default:
      return null; // account and one-off do not take a per-month override
  }
}

const ACCOUNT_ROLES = ["income_hub", "bills", "spending", "savings"] as const;
const FREQUENCIES = ["weekly", "fortnightly", "monthly", "quarterly", "yearly"] as const;
const CARD_KINDS = [
  { value: "credit_card", label: "Credit card" },
  { value: "loan", label: "Loan" },
  { value: "payment_plan", label: "Payment plan" },
] as const;

const CURRENCIES = [
  { code: "GBP", label: "GBP - British Pound" },
  { code: "EUR", label: "EUR - Euro" },
  { code: "USD", label: "USD - US Dollar" },
  { code: "AUD", label: "AUD - Australian Dollar" },
] as const;

const TYPE_LABEL: Record<VulpariNode["type"], string> = {
  account: "Account",
  card: "Card",
  income: "Income",
  bill: "Bill",
  oneoff: "One-off",
  pot: "Pot",
  external: "External",
};

/** A small labelled wrapper for the non-money inputs. */
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs text-ink-muted">{label}</span>
      {children}
    </label>
  );
}

const inputClass =
  "w-full rounded-lg border border-node-border bg-canvas px-2 py-1.5 text-sm text-ink outline-none focus:border-accent";

export function Inspector() {
  // Show only when exactly one node (and no edge) is selected.
  const node = useStore((state) =>
    state.selectedNodeIds.length === 1 && state.selectedEdgeIds.length === 0
      ? state.nodes.find((n) => n.id === state.selectedNodeIds[0])
      : undefined,
  );
  const updateNode = useStore((state) => state.updateNode);
  const deleteNode = useStore((state) => state.deleteNode);
  const selectNode = useStore((state) => state.selectNode);
  const month = useStore((state) => state.context.month);
  const removeFromGroup = useStore((state) => state.removeFromGroup);
  const group = useStore((state) =>
    node ? state.groups.find((g) => g.nodeIds.includes(node.id)) : undefined,
  );

  if (!node) {
    return null;
  }

  // Keyed by node id so all field state resets when the selection moves.
  return (
    <aside
      key={node.id}
      className="pointer-events-auto absolute left-4 top-4 flex w-72 flex-col gap-3 rounded-2xl border border-node-border bg-node-bg/95 p-5 shadow-2xl backdrop-blur"
    >
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wide text-ink-muted">
          {TYPE_LABEL[node.type]}
        </span>
        <button
          type="button"
          onClick={() => selectNode(null)}
          className="text-sm text-ink-muted hover:text-ink"
          aria-label="Close inspector"
        >
          Close
        </button>
      </div>

      <Field label="Name">
        <input
          className={inputClass}
          value={node.name}
          onChange={(event) => updateNode(node.id, { name: event.target.value })}
        />
      </Field>

      <Field label="Currency">
        <select
          className={inputClass}
          value={node.currency}
          onChange={(event) => updateNode(node.id, { currency: event.target.value })}
        >
          {/* Keep any existing currency that is not in the common list. */}
          {CURRENCIES.some((entry) => entry.code === node.currency) ? null : (
            <option value={node.currency}>{node.currency}</option>
          )}
          {CURRENCIES.map((entry) => (
            <option key={entry.code} value={entry.code}>
              {entry.label}
            </option>
          ))}
        </select>
      </Field>

      <TypeFields node={node} onMeta={(meta) => updateNode(node.id, { meta })} />

      <OverrideSection node={node} month={month} onMeta={(meta) => updateNode(node.id, { meta })} />

      {group ? (
        <div className="flex items-center justify-between gap-2 rounded-lg border border-node-border px-3 py-2">
          <span className="text-xs text-ink-muted">
            In group: <span className="text-ink">{group.name}</span>
          </span>
          <button
            type="button"
            onClick={() => void removeFromGroup(node.id)}
            className="text-xs text-ink-muted hover:text-ink"
          >
            Remove
          </button>
        </div>
      ) : null}

      <button
        type="button"
        onClick={() => void deleteNode(node.id)}
        className="mt-2 rounded-lg border border-warning/50 px-3 py-1.5 text-sm text-warning hover:bg-warning/10"
      >
        Delete node
      </button>
    </aside>
  );
}

/** Lets the user vary a node's amount for just the selected month. The default
 * (shared across months) is edited above; this overrides it for one month. */
function OverrideSection({
  node,
  month,
  onMeta,
}: {
  node: VulpariNode;
  month: string;
  onMeta: (meta: VulpariNode["meta"]) => void;
}) {
  const label = OVERRIDE_LABEL[node.type];
  const base = basePrimary(node);
  if (label === undefined || base === null) {
    return null;
  }

  const overrides = (node.meta as { overrides?: Record<string, number> }).overrides ?? {};
  const current = overrides[month];
  const hasOverride = current !== undefined;
  const monthLabel = formatMonthLabel(month);

  const write = (value: number | null) => {
    const next: Record<string, number> = { ...overrides };
    if (value === null) {
      delete next[month];
    } else {
      next[month] = value;
    }
    const cleaned = Object.keys(next).length > 0 ? next : undefined;
    onMeta({ ...node.meta, overrides: cleaned } as VulpariNode["meta"]);
  };

  return (
    <div className="rounded-lg border border-node-border p-2">
      <label className="flex items-center gap-2 text-xs text-ink-muted">
        <input
          type="checkbox"
          checked={hasOverride}
          onChange={(event) => write(event.target.checked ? base : null)}
        />
        Different amount just for {monthLabel}
      </label>
      {hasOverride ? (
        <div className="mt-2">
          <MoneyField
            label={`${label} for ${monthLabel}`}
            pence={current}
            onCommit={(value) => write(value)}
          />
        </div>
      ) : null}
    </div>
  );
}

/** The type-specific fields. Each branch narrows `node`, so `meta` is concrete. */
function TypeFields({
  node,
  onMeta,
}: {
  node: VulpariNode;
  onMeta: (meta: VulpariNode["meta"]) => void;
}) {
  switch (node.type) {
    case "account": {
      const meta = node.meta;
      return (
        <>
          <MoneyField
            label="Balance"
            pence={meta.balance}
            onCommit={(balance) => onMeta({ ...meta, balance })}
          />
          <Field label="Role">
            <select
              className={inputClass}
              value={meta.role}
              onChange={(event) =>
                onMeta({ ...meta, role: event.target.value as (typeof ACCOUNT_ROLES)[number] })
              }
            >
              {ACCOUNT_ROLES.map((role) => (
                <option key={role} value={role}>
                  {role.replace("_", " ")}
                </option>
              ))}
            </select>
          </Field>
        </>
      );
    }
    case "card": {
      const meta = node.meta;
      const kind = meta.kind ?? "credit_card";
      return (
        <>
          <Field label="Kind">
            <select
              className={inputClass}
              value={kind}
              onChange={(event) =>
                onMeta({
                  ...meta,
                  kind: event.target.value as (typeof CARD_KINDS)[number]["value"],
                })
              }
            >
              {CARD_KINDS.map((entry) => (
                <option key={entry.value} value={entry.value}>
                  {entry.label}
                </option>
              ))}
            </select>
          </Field>
          <MoneyField
            label="Owed"
            pence={meta.balance}
            onCommit={(balance) => onMeta({ ...meta, balance })}
          />
          <MoneyField
            label={kind === "credit_card" ? "Credit limit" : "Original amount"}
            pence={meta.creditLimit}
            onCommit={(creditLimit) => onMeta({ ...meta, creditLimit })}
          />
          {kind === "credit_card" ? (
            <>
              <MoneyField
                label="Statement balance"
                pence={meta.statementBalance}
                onCommit={(statementBalance) => onMeta({ ...meta, statementBalance })}
              />
              <MoneyField
                label="Minimum payment"
                pence={meta.minPayment}
                onCommit={(minPayment) => onMeta({ ...meta, minPayment })}
              />
            </>
          ) : null}
          <MoneyField
            label={kind === "credit_card" ? "Payment this month" : "Monthly payment"}
            pence={meta.payment ?? meta.statementBalance}
            onCommit={(payment) => onMeta({ ...meta, payment })}
          />
          {kind !== "payment_plan" ? (
            <Field label="APR (%)">
              <input
                className={`${inputClass} figure`}
                inputMode="decimal"
                defaultValue={(meta.aprBasisPoints / 100).toString()}
                onChange={(event) => {
                  const percent = Number.parseFloat(event.target.value);
                  if (!Number.isNaN(percent)) {
                    onMeta({ ...meta, aprBasisPoints: Math.round(percent * 100) });
                  }
                }}
              />
            </Field>
          ) : null}
          <Field label="Due day (1 to 31)">
            <DayInput value={meta.dueDay} onChange={(dueDay) => onMeta({ ...meta, dueDay })} />
          </Field>
        </>
      );
    }
    case "income": {
      const meta = node.meta;
      return (
        <>
          <MoneyField
            label="Amount"
            pence={meta.amount}
            onCommit={(amount) => onMeta({ ...meta, amount })}
          />
          <Field label="Pay day (1 to 31)">
            <DayInput value={meta.payDay} onChange={(payDay) => onMeta({ ...meta, payDay })} />
          </Field>
        </>
      );
    }
    case "bill": {
      const meta = node.meta;
      return (
        <>
          <MoneyField
            label="Amount"
            pence={meta.amount}
            onCommit={(amount) => onMeta({ ...meta, amount })}
          />
          <Field label="Frequency">
            <select
              className={inputClass}
              value={meta.frequency}
              onChange={(event) =>
                onMeta({ ...meta, frequency: event.target.value as (typeof FREQUENCIES)[number] })
              }
            >
              {FREQUENCIES.map((frequency) => (
                <option key={frequency} value={frequency}>
                  {frequency}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Due day (1 to 31)">
            <DayInput value={meta.dueDay} onChange={(dueDay) => onMeta({ ...meta, dueDay })} />
          </Field>
        </>
      );
    }
    case "oneoff": {
      const meta = node.meta;
      return (
        <>
          <MoneyField
            label="Amount"
            pence={meta.amount}
            onCommit={(amount) => onMeta({ ...meta, amount })}
          />
          <Field label="Due date">
            <input
              type="date"
              className={`${inputClass} figure`}
              value={meta.dueDate}
              onChange={(event) => onMeta({ ...meta, dueDate: event.target.value })}
            />
          </Field>
        </>
      );
    }
    case "pot": {
      const meta = node.meta;
      return (
        <>
          <MoneyField
            label="Balance"
            pence={meta.balance}
            onCommit={(balance) => onMeta({ ...meta, balance })}
          />
          <MoneyField
            label="Target"
            pence={meta.target}
            onCommit={(target) => onMeta({ ...meta, target })}
          />
          <MoneyField
            label="Monthly top-up"
            pence={meta.topUp ?? 0}
            onCommit={(topUp) => onMeta({ ...meta, topUp })}
          />
        </>
      );
    }
    case "external": {
      const meta = node.meta;
      return (
        <MoneyField
          label="Amount paid each month"
          pence={meta.amount ?? 0}
          onCommit={(amount) => onMeta({ ...meta, amount })}
        />
      );
    }
  }
}

/** A constrained day-of-month number input. */
function DayInput({ value, onChange }: { value: number; onChange: (value: number) => void }) {
  return (
    <input
      type="number"
      min={1}
      max={31}
      className={`${inputClass} figure`}
      value={value}
      onChange={(event) => {
        const day = Number.parseInt(event.target.value, 10);
        if (!Number.isNaN(day) && day >= 1 && day <= 31) {
          onChange(day);
        }
      }}
    />
  );
}
